import {singleMemo, weakMemo} from '@packages/utilities/memo';
import {emptyArray} from '@packages/utilities/fp/utils';
import type {StyleBlockStateType} from '@packages/shared/LegacyStyleSystem/states/StyleBlockStateTypes';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import {createCurrentVariationDataAttribute} from '@packages/systems/optimization/utils';
import {Expressions} from '@packages/systems/wfdl';
import {parseVariantKey} from '@packages/systems/style-legacy/variants';
import {getOrderedBreakpointIdsByInheritanceRules} from '@packages/systems/style/media-queries';

const EMPTY: string[] = emptyArray;

export function getOptimizationStyleVariations({
  element,
  variationId,
  mediaQuery = 'main',
  styleBlockState,
}: {
  element?: ExpressionElement | null;
  variationId?: string | null;
  mediaQuery?: BreakpointId;
  styleBlockState: StyleBlockStateType;
}) {
  if (!variationId || !element) {
    return EMPTY;
  }

  return memoizedGetOptimizationStyleVariations(variationId)(mediaQuery)(
    styleBlockState
  )(element);
}

const getBreakpoints = singleMemo((currentMediaQuery: BreakpointId) => {
  const breakpoints =
    getOrderedBreakpointIdsByInheritanceRules(currentMediaQuery);
  const currentIndex = breakpoints.indexOf(currentMediaQuery);
  return breakpoints.slice(0, currentIndex + 1);
});

const memoizedGetOptimizationStyleVariations = singleMemo(
  (variationId: string) =>
    singleMemo((currentMediaQuery: BreakpointId) =>
      weakMemo((styleBlockState: StyleBlockStateType) =>
        weakMemo((element: ExpressionElement) => {
          const elementData = Expressions.getElementData(element);
          const styleBlockIds = Expressions.getIn(elementData, [
            {in: 'Record', at: 'styleBlockIds'},
          ]);

          if (!styleBlockIds) {
            return EMPTY;
          }

          const styleBlockIdsList = Expressions.getList(styleBlockIds);

          if (!styleBlockIdsList) {
            return EMPTY;
          }

          const variations: string[] = [];
          const breakpoints = getBreakpoints(currentMediaQuery);
          const optimizationAttr =
            createCurrentVariationDataAttribute(variationId);

          for (const styleBlockIdItem of styleBlockIdsList) {
            const styleBlockId = Expressions.getText(styleBlockIdItem);

            if (!styleBlockId) {
              continue;
            }

            const styleBlock = styleBlockState.getBlock(styleBlockId);

            if (!styleBlock) {
              continue;
            }

            // Search all variant keys for ones containing the optimization
            // attribute. This catches both plain optimization variants
            // (e.g. `main_data-intellimize-var-123`) and combined variants
            // that also include a component style variant variantState
            // (e.g. `main_fieldId=optionId_data-intellimize-var-123`).
            styleBlock.variants.keySeq().forEach((variantKey: string) => {
              if (!variantKey.includes(optimizationAttr)) {
                return;
              }
              const parsed = parseVariantKey(variantKey);
              if (breakpoints.includes(parsed.mediaQuery)) {
                variations.push(variantKey);
              }
            });
          }

          return variations.length ? variations : EMPTY;
        })
      )
    )
);
